import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import NavigationHeader from '../homepage/components/NavigationHeader';
import Button from '../../components/ui/Button';
import SpeciesCard from './components/SpeciesCard';
import PopulationTrendChart from './components/PopulationTrendChart';
import ThreatIndicator from './components/ThreatIndicator';
import ConservationActionCard from './components/ConservationActionCard';
import SpeciesDetailModal from './components/SpeciesDetailModal';
import FilterPanel from './components/FilterPanel';
import StatCard from './components/StatCard';
import TurtleHatchlingTable from './components/TurtleHatchlingTable';
import TurtleNestingTable from './components/TurtleNestingTable';

const BiodiversityStatusCenter = () => {
  const [selectedSpecies, setSelectedSpecies] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    threatLevel: 'all',
    trend: 'all',
    habitat: 'all'
  });

  const statsData = [
  {
    icon: 'Leaf',
    iconBg: 'bg-success/20',
    iconColor: 'text-success',
    value: '1,247',
    label: 'Total Species Monitored',
    subtitle: 'Across all habitats',
    change: 3.2
  },
  {
    icon: 'AlertTriangle',
    iconBg: 'bg-error/20',
    iconColor: 'text-error',
    value: '89',
    label: 'Critically Endangered',
    subtitle: 'Requiring immediate action',
    change: -5.4
  },
  {
    icon: 'TrendingUp',
    iconBg: 'bg-info/20',
    iconColor: 'text-info',
    value: '342',
    label: 'Population Increasing',
    subtitle: 'Conservation success stories',
    change: 12.8
  },
  {
    icon: 'Shield',
    iconBg: 'bg-primary/20',
    iconColor: 'text-primary',
    value: '23',
    label: 'Active Conservation Programs',
    subtitle: 'Ongoing protection efforts'
  }];


  const speciesData = [
  {
    id: 7,
    commonName: 'Water Monitor Lizard',
    scientificName: 'Varanus salvator',
    image: "/assets/images/UNDERGROUND_-_6-1769151502955.jpg",
    imageAlt: 'Monitor lizard in natural cave rocky habitat within PPSRNP showing native reptile species thriving in protected environment',
    threatLevel: 'Vulnerable',
    population: '500-800 individuals',
    populationTrend: 'stable',
    lastSurvey: 'Jan 2026',
    primaryThreat: 'Habitat disturbance',
    habitat: 'Forest',
    protectedAreas: '15 sites',
    fullDescription: 'Water Monitor lizards are one of PPSRNP\'s most impressive residents, growing over 6 feet in length. These magnificent reptiles have thrived in the park\'s diverse ecosystems for millions of years, serving as both predator and scavenger in the delicate balance of the limestone karst environment. Often found near water sources and cave entrances, these ancient guardians play a crucial role in maintaining ecosystem health by controlling rodent populations and cleaning up carrion.',
    conservationActions: [
      'Habitat protection in cave systems',
      'Population monitoring programs',
      'Anti-poaching enforcement',
      'Ecosystem health indicators',
      'Wildlife corridor maintenance'
    ]
  },
  {
    id: 8,
    commonName: 'Philippine Cockatoo',
    scientificName: 'Cacatua haematuropygia',
    image: "/assets/images/UNDERGROUND_-_6-1769151502955.jpg",
    imageAlt: 'Philippine Cockatoo with white plumage and distinctive coral-red underwing feathers displaying in natural forest habitat',
    threatLevel: 'Critical',
    population: '800-1,000 individuals',
    populationTrend: 'increasing',
    lastSurvey: 'Dec 2025',
    primaryThreat: 'Illegal wildlife trade',
    habitat: 'Forest',
    protectedAreas: '18 sites',
    fullDescription: 'The critically endangered Philippine Cockatoo represents one of conservation\'s greatest success stories in Palawan. Once numbering fewer than 1,000 individuals worldwide, this magnificent white cockatoo with distinctive coral-red underwing feathers thrives within PPSRNP\'s protected boundaries. The park\'s pristine primary forest habitat provides crucial nesting sites and food sources for these intelligent birds, whose haunting calls echo through the limestone karst landscape.',
    conservationActions: [
      'Anti-poaching patrols',
      'Nesting site protection',
      'Community education programs',
      'Habitat restoration',
      'Population recovery monitoring'
    ]
  }];


  const populationTrendData = [
    { year: '2020', population: 2800 },
    { year: '2021', population: 2650 },
    { year: '2022', population: 2420 },
    { year: '2023', population: 2180 },
    { year: '2024', population: 1950 },
    { year: '2025', population: 1720 }
  ];


  const threatsData = [
  {
    type: 'Illegal Logging Activity',
    severity: 'Critical',
    description: 'Unauthorized logging detected in Zone 3A affecting primary forest habitat critical for Philippine Eagle nesting.',
    affectedSpecies: '12 species',
    detectedDate: '05 Dec 2025',
    actionPlan: 'Immediate ranger deployment and law enforcement coordination initiated. Satellite monitoring activated.'
  },
  {
    type: 'Seagrass Meadow Degradation',
    severity: 'High',
    description: 'Significant decline in seagrass coverage observed in coastal zone affecting Dugong feeding grounds.',
    affectedSpecies: '3 species',
    detectedDate: '02 Dec 2025',
    actionPlan: 'Water quality assessment scheduled. Community consultation for fishing practice modifications.'
  },
  {
    type: 'Disease Outbreak Risk',
    severity: 'Medium',
    description: 'Increased cattle presence near Tamaraw habitat raises disease transmission concerns.',
    affectedSpecies: '1 species',
    detectedDate: '28 Nov 2025',
    actionPlan: 'Veterinary surveillance increased. Buffer zone enforcement strengthened.'
  },
  {
    type: 'Climate-Induced Habitat Shift',
    severity: 'High',
    description: 'Temperature changes affecting species distribution patterns and breeding cycles.',
    affectedSpecies: '8 species',
    detectedDate: '25 Nov 2025',
    actionPlan: 'Long-term monitoring protocol established. Habitat adaptation strategies under development.'
  }];


  const conservationActionsData = [
  {
    icon: 'Shield',
    title: 'Philippine Eagle Recovery Program',
    targetSpecies: 'Pithecophaga jefferyi',
    description: 'Comprehensive conservation initiative combining habitat protection, captive breeding, and community engagement to stabilize and increase Philippine Eagle populations.',
    status: 'Active',
    startDate: '15 Jan 2024',
    duration: '5 years',
    budget: '₱12.5M',
    progress: 68,
    teamSize: 24
  },
  {
    icon: 'Waves',
    title: 'Seagrass Restoration Initiative',
    targetSpecies: 'Dugong dugon',
    description: 'Large-scale seagrass meadow restoration project to rebuild critical feeding habitats for Dugong populations and associated marine biodiversity.',
    status: 'Active',
    startDate: '01 Mar 2024',
    duration: '3 years',
    budget: '₱8.2M',
    progress: 45,
    teamSize: 18
  },
  {
    icon: 'TreePine',
    title: 'Forest Corridor Connectivity',
    targetSpecies: 'Multiple species',
    description: 'Establishing wildlife corridors connecting fragmented forest patches to enable genetic exchange and reduce isolation of endangered populations.',
    status: 'Planning',
    startDate: '01 Feb 2026',
    duration: '4 years',
    budget: '₱15.8M',
    progress: 12,
    teamSize: 32
  }];


  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      threatLevel: 'all',
      trend: 'all',
      habitat: 'all'
    });
  };

  const filteredSpecies = speciesData?.filter((species) => {
    const matchesSearch = species?.commonName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
    species?.scientificName?.toLowerCase()?.includes(filters?.search?.toLowerCase());
    const matchesThreat = filters?.threatLevel === 'all' || species?.threatLevel === filters?.threatLevel;
    const matchesTrend = filters?.trend === 'all' || species?.populationTrend === filters?.trend;
    const matchesHabitat = filters?.habitat === 'all' || species?.habitat === filters?.habitat;

    return matchesSearch && matchesThreat && matchesTrend && matchesHabitat;
  });

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Navigation Header */}
      <NavigationHeader />
      
      {/* Add padding to account for fixed header */}
      <div className="pt-20">
        {/* Statistics Overview */}
        <section className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statsData?.map((stat, index) =>
            <StatCard key={index} stat={stat} />
            )}
          </div>
        </section>

        {/* Marine Turtle Conservation Section */}
        <section className="mb-8">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-foreground mb-2">Marine Turtle Conservation Program</h2>
            <p className="text-sm text-muted-foreground">
              Comprehensive monitoring data for marine turtle conservation efforts in the Puerto Princesa Subterranean River National Park
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6">
            <TurtleHatchlingTable />
            <TurtleNestingTable />
          </div>
        </section>

        {/* Population Trend Chart */}
        <section className="mb-8">
          <PopulationTrendChart
            data={populationTrendData}
            title="Palawan Peacock-Pheasant Population Trend (2020-2025)" />

        </section>

        {/* Active Threats Section */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-foreground">Active Threats</h2>
            <Link to="/emergency-alert-system">
              <Button variant="outline" size="sm" iconName="AlertCircle" iconPosition="left">
                View All Alerts
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {threatsData?.map((threat, index) =>
            <ThreatIndicator key={index} threat={threat} />
            )}
          </div>
        </section>

        {/* Conservation Actions */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-6">Active Conservation Programs</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {conservationActionsData?.map((action, index) =>
            <ConservationActionCard key={index} action={action} />
            )}
          </div>
        </section>

        {/* Species Monitoring Section */}
        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-6">Species Monitoring Database</h2>
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Filter Panel */}
            <div className="lg:col-span-1">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters} />

            </div>

            {/* Species Grid */}
            <div className="lg:col-span-3">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {filteredSpecies?.length} of {speciesData?.length} species
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" iconName="Grid3x3">
                    Grid
                  </Button>
                  <Button variant="ghost" size="sm" iconName="List">
                    List
                  </Button>
                </div>
              </div>

              {filteredSpecies?.length > 0 ?
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredSpecies?.map((species) =>
                <SpeciesCard
                  key={species?.id}
                  species={species}
                  onClick={() => setSelectedSpecies(species)} />

                )}
                </div> :

              <div className="bg-card rounded-lg p-12 text-center">
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No species found</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Try adjusting your filters or search terms
                  </p>
                  <Button variant="outline" onClick={handleResetFilters}>
                    Reset Filters
                  </Button>
                </div>
              }
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="mt-12 bg-gradient-conservation rounded-lg p-8">
          <h2 className="text-2xl font-semibold text-foreground mb-6">Conservation Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/research-data-portal" className="bg-card rounded-lg p-6 hover:shadow-conservation-lg animation-organic">
              <Icon name="Database" size={32} className="text-primary mb-3" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Research Data Portal</h3>
              <p className="text-sm text-muted-foreground">Access scientific datasets and publications</p>
            </Link>
            <Link to="/interactive-conservation-map" className="bg-card rounded-lg p-6 hover:shadow-conservation-lg animation-organic">
              <Icon name="Map" size={32} className="text-secondary mb-3" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Conservation Map</h3>
              <p className="text-sm text-muted-foreground">View spatial distribution and habitat zones</p>
            </Link>
            <Link to="/community-engagement-platform" className="bg-card rounded-lg p-6 hover:shadow-conservation-lg animation-organic">
              <Icon name="Users" size={32} className="text-info mb-3" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Community Programs</h3>
              <p className="text-sm text-muted-foreground">Join conservation volunteer initiatives</p>
            </Link>
          </div>
        </section>
      </div>
      {/* Species Detail Modal */}
      {selectedSpecies &&
      <SpeciesDetailModal
        species={selectedSpecies}
        onClose={() => setSelectedSpecies(null)} />

      }
      {/* Footer */}
      <footer className="bg-card border-t border-border mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">PPSRNP Digital Platform</h3>
              <p className="text-sm text-muted-foreground">
                Protecting irreplaceable biodiversity through science and community engagement.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Conservation</h4>
              <ul className="space-y-2">
                <li><Link to="/biodiversity-status-center" className="text-sm text-muted-foreground hover:text-foreground animation-organic">Species Monitoring</Link></li>
                <li><Link to="/interactive-conservation-map" className="text-sm text-muted-foreground hover:text-foreground animation-organic">Conservation Map</Link></li>
                <li><Link to="/emergency-alert-system" className="text-sm text-muted-foreground hover:text-foreground animation-organic">Threat Alerts</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Research</h4>
              <ul className="space-y-2">
                <li><Link to="/research-data-portal" className="text-sm text-muted-foreground hover:text-foreground animation-organic">Data Portal</Link></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-foreground animation-organic">Publications</a></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-foreground animation-organic">Collaborate</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Get Involved</h4>
              <ul className="space-y-2">
                <li><Link to="/community-engagement-platform" className="text-sm text-muted-foreground hover:text-foreground animation-organic">Volunteer Programs</Link></li>
                <li><Link to="/visitor-planning-hub" className="text-sm text-muted-foreground hover:text-foreground animation-organic">Visit the Park</Link></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-foreground animation-organic">Donate</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} PPSRNP Digital Platform. UNESCO World Heritage Site.
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-muted-foreground hover:text-foreground animation-organic">
                <Icon name="Facebook" size={20} />
              </a>
              <a href="#" className="text-muted-foreground hover:text-foreground animation-organic">
                <Icon name="Twitter" size={20} />
              </a>
              <a href="#" className="text-muted-foreground hover:text-foreground animation-organic">
                <Icon name="Instagram" size={20} />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BiodiversityStatusCenter;